import React from 'react';

import {
  newQuizItemId,
  ResultSummaryConfig,
  SummaryDetail,
} from '../../CustomBlocks/quiz/quizTypes';
import { SettingsGroup, SettingsRow } from '../slotBookingEditor/settingsUi';
import { ListSection, TextSetting } from './quizEditorUi';

interface ResultSummaryEditorProps {
  config: ResultSummaryConfig;
  onChange: (patch: Partial<ResultSummaryConfig>) => void;
}

const ResultSummaryEditor: React.FC<ResultSummaryEditorProps> = ({ config, onChange }) => (
  <>
    <SettingsGroup
      title="Header"
      footer={
        <>
          Use <code>{'{{field_name}}'}</code> to insert an answer, also inside the image URL
          (e.g. <code>{'.../{{gender}}.png'}</code>).
        </>
      }
    >
      <TextSetting label="Title" value={config.title} onChange={(title) => onChange({ title })} />
      <TextSetting
        label="Image URL"
        placeholder="https://example.com/result.png"
        value={config.imageUrl}
        onChange={(imageUrl) => onChange({ imageUrl })}
      />
    </SettingsGroup>

    <SettingsGroup title="Level meter">
      <TextSetting
        label="Meter title"
        value={config.meterTitle}
        onChange={(meterTitle) => onChange({ meterTitle })}
      />
      <TextSetting
        label="Current level badge"
        value={config.meterBadge}
        onChange={(meterBadge) => onChange({ meterBadge })}
      />
      <SettingsRow label="Scale labels" stacked>
        <div className="qe-inline">
          <input
            type="text"
            className="sb-text-input"
            value={config.lowLabel}
            placeholder="Low"
            onChange={(e) => onChange({ lowLabel: e.target.value })}
          />
          <input
            type="text"
            className="sb-text-input"
            value={config.midLabel}
            placeholder="Middle"
            onChange={(e) => onChange({ midLabel: e.target.value })}
          />
          <input
            type="text"
            className="sb-text-input"
            value={config.highLabel}
            placeholder="High"
            onChange={(e) => onChange({ highLabel: e.target.value })}
          />
        </div>
      </SettingsRow>
      <SettingsRow label="Marker position" description={`${config.meterPosition}% along the scale`}>
        <input
          type="range"
          className="qe-range"
          min={0}
          max={100}
          value={config.meterPosition}
          aria-label="Marker position"
          onChange={(e) => onChange({ meterPosition: Number(e.target.value) })}
        />
      </SettingsRow>
    </SettingsGroup>

    <SettingsGroup title="Insight">
      <TextSetting
        label="Insight title"
        value={config.insightTitle}
        onChange={(insightTitle) => onChange({ insightTitle })}
      />
      <TextSetting
        label="Insight text"
        multiline
        value={config.insightText}
        onChange={(insightText) => onChange({ insightText })}
      />
    </SettingsGroup>

    <ListSection<SummaryDetail>
      title="Key facts"
      itemName="Fact"
      items={config.details}
      create={() => ({ id: newQuizItemId(), icon: '⭐', label: 'Label', text: 'Value' })}
      onChange={(details) => onChange({ details })}
      renderItem={(detail, update) => (
        <div className="qe-inline">
          <input
            type="text"
            className="sb-text-input qe-short-input"
            value={detail.icon}
            maxLength={8}
            aria-label="Icon"
            onChange={(e) => update({ icon: e.target.value })}
          />
          <input
            type="text"
            className="sb-text-input"
            value={detail.label}
            placeholder="Label"
            onChange={(e) => update({ label: e.target.value })}
          />
          <input
            type="text"
            className="sb-text-input"
            value={detail.text}
            placeholder="Value or {{field_name}}"
            onChange={(e) => update({ text: e.target.value })}
          />
        </div>
      )}
    />
  </>
);

export default ResultSummaryEditor;
